import { isBlank } from './detailShared'
// Cómo se NOMBRA una operación en la ficha, en un solo sitio.
//
// ⚠ Defecto visto por el PO con el volcado de volumen (orden `ofref2p`): la cabecera pintaba
// literalmente "null - null". La plantilla era `${jobOrder} - ${name}` y la guarda vigilaba
// `jobReferenceId` —que venía informado— con un `!== undefined` que PASA con `null`. Dos errores
// encadenados: se comprobaba un campo distinto del que se iba a usar, y se confundía ausente con
// nulo, que es la misma confusión del addendum 7 (E8).
//
// La regla vive aquí y no en cada plantilla porque el mismo par viajaba interpolado en dos sitios
// —el indicador de Estado y el origen de un material— y el segundo no tenía ninguna guarda.
//
// Es una función PURA: devuelve texto ya compuesto o una clave i18n para que la traduzca quien
// pinta. Así se prueba sin montar nada y no arrastra el hook de traducción.

/**
 * @param {object} operation `{jobOrder, name}` tal cual llega del servidor; cualquiera de los dos
 * puede ser `null` (el `jobOrder` nulo es legítimo: la columna está vacía para algunas operaciones).
 * @param {string} fallbackKey clave i18n para cuando no hay NI nombre NI número. La elige el
 * llamante porque el significado depende del sitio: bajo "Estado" hay una operación en curso que no
 * sabemos nombrar; en el origen de un material, una operación sin nombre.
 * @returns {{text: string, key: string, values: object}} `text` ya listo, o `key`+`values` a traducir.
 */
export const operationNameLabel = (operation, fallbackKey) => {
  const source = operation || {}
  const hasName = !isBlank(source.name)
  const hasJobOrder = !isBlank(source.jobOrder)

  // Con los dos, el rótulo de siempre.
  if (hasName && hasJobOrder) {
    return { text: `${source.jobOrder} - ${source.name}`, key: '', values: {} }
  }
  // Solo el nombre: el número de orden de trabajo no añade nada que el nombre no diga, y un guion
  // suelto delante solo hace preguntarse qué falta.
  if (hasName) {
    return { text: `${source.name}`, key: '', values: {} }
  }
  // Solo el número: se dice CON su rótulo. Un número suelto no se entiende, y callarlo perdería el
  // único dato que identifica la operación.
  if (hasJobOrder) {
    return { text: '', key: 'Operation {{jobOrder}}', values: { jobOrder: source.jobOrder } }
  }
  return { text: '', key: fallbackKey, values: {} }
}

/**
 * ADDENDUM 20 (E26) — cómo se identifica una operación. ENMIENDA al addendum 17.
 *
 * Regla del PO: «o tiene code o tiene name; si falta alguno se muestra el otro, y si están los dos
 * y el texto que tienen no es el mismo se muestran los dos». Cuatro ramas:
 *
 *   los dos y distintos -> los dos      |  falta el nombre -> el código
 *   los dos e iguales   -> uno          |  falta el código -> EL NOMBRE   (rama nueva)
 *
 * ⚠️ El cambio de fondo no es la rama nueva: es que NINGUNO DE LOS DOS ES YA EL EJE. En el addendum
 * 17 el código mandaba y el nombre era el añadido opcional — por eso la propiedad se llamaba
 * `showsName`, "¿aporta algo el nombre?". Esa forma ya mentiría, así que se devuelve `identifier`
 * («lo que identifica a esta operación, sea lo que sea que haya llegado») y `showsBoth`. El nombre
 * ES el contrato (R-HANDOFF-2): dejar `showsName` habría dejado escrita en el contrato una asimetría
 * que el PO acaba de quitar.
 *
 * Lo que lo provocó, y conviene no perderlo: en materiales, con el nodo sin código, Sun servía el
 * NOMBRE DE LA TECNOLOGÍA dentro de un campo llamado `code` — un valor de otra cosa con el nombre de
 * esta. Esa es la razón de que la ficha no pueda dar por hecho que `code` viene informado.
 *
 * Lo que el addendum 17 acertó y aquí SIGUE EN PIE:
 *
 * ⚠️ La ausencia no cuenta como "distinto" (RN8). Con dos ramas —iguales uno, distintos los dos— un
 * `name` nulo saldría como distinto y se pintarían dos columnas con la del nombre vacía en 2.906 de
 * 2.907 filas: la columna vacía movida de sitio, que es lo que el encargo venía a quitar.
 *
 * ⚠️ La comparación es EXACTA y sin normalizar: ni mayúsculas, ni espacios, ni acentos. `'OP-030 '`
 * NO es `'OP-030'`, y se muestran los dos. El dato lo dice y no adivinamos intenciones.
 *
 * ⚠️ Un blanco NO se filtra aquí: Sun ya convierte la cadena vacía y la de solo espacios en `null`,
 * en un único sitio para las tres puertas. Repetir esa regla aquí la duplicaría —y fue precisamente
 * faltar en una de las tres lo que estuvo a punto de pintar una segunda columna vacía—.
 *
 * @param {object} operation `{code, name}` tal cual llega de N3 o de N1
 * @returns {{code: string, name: string, identifier: string, showsBoth: boolean}} `identifier` es lo
 * que se muestra cuando solo se muestra una cosa; `showsBoth` dice si hacen falta las dos.
 */
export const operationCodeAndName = (operation) => {
  const source = operation || {}
  const code = isBlank(source.code) ? '' : String(source.code)
  const name = isBlank(source.name) ? '' : String(source.name)
  // El código manda cuando está — es lo que identifica una operación para quien trabaja con ella—,
  // pero ya no es imprescindible: sin él, identifica el nombre. Sin ninguno de los dos, `''`, y el
  // llamante decide qué decir; aquí no se inventa un rótulo.
  return { code, name, identifier: code || name, showsBoth: code !== '' && name !== '' && name !== code }
}

/**
 * S353/S354 — la regla simétrica del ADDENDUM 20 compuesta en texto: «código - nombre» si hacen
 * falta los dos, y si no lo que identifica a la operación. Una sola función para Progress y
 * Materiales: dos copias de la misma regla son dos pantallas que acaban discrepando.
 */
export const operationLabelOf = (operation) => {
  const { code, name, identifier, showsBoth } = operationCodeAndName(operation)
  return showsBoth ? `${code} - ${name}` : identifier
}
